import { IconMapPin } from 'central-icons/IconMapPin';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconButton, mergeRefs, Popover, Text } from '@revelio/ui';

import type { ScreenerTableRow } from '../../DataProvider/utils/transform-screener-table-data';
import { EmailPopover } from './ScreenerTablePopover/EmailPopover';
import { CompanyIcon } from './ScreenerTablePopover/icons/company-icon';
import { EmailIcon } from './ScreenerTablePopover/icons/email-icon';
import { LinkedinIcon } from './ScreenerTablePopover/icons/linkedin-icon';
import styles from './ScreenerTablePopover.module.css';
import layoutStyles from './screener-table-popover.module.css';

const POPOVER_OPEN_DELAY_MS = 500;
const POPOVER_CLOSE_DELAY_MS = 100;

export interface ScreenerTablePopoverProps {
  data: ScreenerTableRow;
  children: JSX.Element | JSX.Element[];
}

export const ScreenerTablePopover = ({
  data,
  children,
}: ScreenerTablePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLTableRowElement | null>(null);
  const hiddenAnchorRef = useRef<HTMLDivElement | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (anchorRef.current && hiddenAnchorRef.current && isOpen) {
      const rect = anchorRef.current.getBoundingClientRect();
      hiddenAnchorRef.current.style.top = `${rect.top}px`;
      hiddenAnchorRef.current.style.left = `${rect.left}px`;
      hiddenAnchorRef.current.style.width = `${rect.width}px`;
      hiddenAnchorRef.current.style.height = `${rect.height}px`;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  if (!data) return children;

  const {
    year_founded,
    phone_number,
    linkedin_url,
    email = 'Not available',
    description,
    short_name = '',
    hq_metro_area,
    hq_country,
    website,
  } = data;

  const href = website ? `https://${website}` : undefined;
  const childElement = Array.isArray(children) ? children[0] : children;

  if (
    !React.isValidElement<
      React.HTMLAttributes<HTMLTableRowElement> & {
        ref?: React.Ref<HTMLTableRowElement>;
      }
    >(childElement)
  ) {
    return <>{children}</>;
  }

  const originalRef = childElement.props.ref;

  const triggerElement = React.cloneElement(childElement, {
    ref: mergeRefs([
      (node: HTMLTableRowElement | null) => {
        anchorRef.current = node;
      },
      originalRef,
    ]),
    onMouseEnter: () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      if (hiddenAnchorRef.current && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        hiddenAnchorRef.current.style.top = `${rect.top}px`;
        hiddenAnchorRef.current.style.left = `${rect.left}px`;
        hiddenAnchorRef.current.style.width = `${rect.width}px`;
        hiddenAnchorRef.current.style.height = `${rect.height}px`;
      }
      openTimeoutRef.current = setTimeout(() => {
        setIsOpen(true);
      }, POPOVER_OPEN_DELAY_MS);
    },
    onMouseLeave: () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, POPOVER_CLOSE_DELAY_MS);
    },
  });

  return (
    <>
      {triggerElement}
      {createPortal(
        <Popover
          placement="bottom"
          arrow
          open={isOpen}
          onOpenChange={setIsOpen}
          className={styles.content}
          // Radix positions against its trigger, and this popover has no real
          // one: the row it describes is a <tr> the surface cannot be nested
          // inside, so an empty box is parked over the row's client rect (see
          // the hover handlers above) and stands in as the anchor.
          trigger={<div ref={hiddenAnchorRef} className={styles.anchor} />}
          // Chakra's `closeOnBlur={false}`: the row's hover timers own this
          // card's lifetime. Radix would otherwise dismiss on any pointer-down
          // outside the surface — including a click on the very row being
          // described, which is "outside" by that measure.
          onInteractOutside={(event) => event.preventDefault()}
          // A hover card must not pull focus off what the user is doing, and
          // the stand-in anchor is not focusable to hand it back to.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onMouseEnter={() => {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
              closeTimeoutRef.current = null;
            }
            if (openTimeoutRef.current) {
              clearTimeout(openTimeoutRef.current);
            }
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            if (openTimeoutRef.current) {
              clearTimeout(openTimeoutRef.current);
              openTimeoutRef.current = null;
            }
            closeTimeoutRef.current = setTimeout(() => {
              setIsOpen(false);
            }, POPOVER_CLOSE_DELAY_MS);
          }}
        >
          <div className={styles.header}>
            <div className={layoutStyles.actions}>
              <a href={href} target="_blank" rel="noreferrer">
                <IconButton
                  aria-label="Company"
                  icon={<CompanyIcon height={40} width={40} bottom={3} />}
                  size="lg"
                  shape="rounded"
                  className={layoutStyles.iconAction}
                />
              </a>
              <div className={layoutStyles.details}>
                <div className={layoutStyles.nameRow}>
                  <a href={href} target="_blank" rel="noreferrer">
                    <Text
                      as="span"
                      fontSize="sm"
                      weight="semibold"
                      className={styles.name}
                      data-linked={href ? true : undefined}
                    >
                      {short_name}
                    </Text>
                  </a>
                  <div className={layoutStyles.spacer} />
                  <div className={layoutStyles.socialLinks}>
                    {linkedin_url && (
                      <IconButton
                        aria-label="LinkedIn"
                        size="sm"
                        className={layoutStyles.iconAction}
                        icon={<LinkedinIcon />}
                        onClick={() => {
                          window.open(
                            `${linkedin_url}`,
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                      />
                    )}
                    {email && (
                      <EmailPopover
                        emails={{
                          email: String(email),
                        }}
                        trigger={
                          <IconButton
                            aria-label="Email"
                            icon={<EmailIcon />}
                            size="sm"
                            className={layoutStyles.iconAction}
                          />
                        }
                      />
                    )}
                  </div>
                </div>
                <div className={layoutStyles.titleRow}>
                  <Text fontSize="x-sm" className={styles.muted}>
                    <IconMapPin
                      size="0.625rem"
                      className={styles.locationIcon}
                    />
                    {[hq_metro_area, hq_country].join(', ') || 'Not available'}
                  </Text>
                  <div className={layoutStyles.spacer} />
                  <div className={layoutStyles.infoField}>
                    <Text fontSize="x-sm" className={styles.muted}>
                      {year_founded || phone_number}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <Text as="span" fontSize="x-sm" className={styles.muted}>
              {description || 'No description available'}
            </Text>
          </div>
        </Popover>,
        document.body
      )}
    </>
  );
};
